import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/apiClient'
import { mapProfile } from '@/lib/mappers'
import { useAuth } from '@/providers/AuthProvider'
import type { Profile } from '@/types/profile'

/**
 * Hook to fetch user profile via Hono API.
 * Waits for an authenticated session so /api/profiles/me is not called unauthenticated.
 */
export const useUserProfile = () => {
  const { user, loading: authLoading } = useAuth()

  const { data, isLoading, error, refetch } = useQuery<Profile>({
    queryKey: ['user-profile', user?.id],
    enabled: !authLoading && Boolean(user?.id),
    queryFn: async () => {
      const res = await api.get<{ user: { email?: string }; profile: Record<string, unknown> }>(
        '/api/profiles/me',
      )
      const mapped = mapProfile(res.profile, res.user?.email)
      if (!mapped) throw new Error('Profile not found')
      return mapped
    },
    staleTime: 30_000,
    gcTime: 300_000,
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
    retry: 1,
  })

  return {
    data,
    isLoading: authLoading || (Boolean(user?.id) && isLoading),
    error,
    refetch,
  }
}
